import { FC, useState, useEffect, useRef, Fragment, useMemo } from 'react';
import { ArrowUpToLineIcon, MapPinIcon } from 'lucide-react';
import { useNavigate } from 'react-router';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';

import { NbButton, NbCard } from '@/components/Neobrut';
import { useFetchSolvedCases } from '@/hooks/useFetchSolvedCases';
import { SolvedCasesSkeleton } from '@/components/Skeletons';

export const SolvedCases: FC = () => {
  const titleRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useFetchSolvedCases();

  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      const yPosition = e.touches[0].clientY;
      setShowScrollButton(yPosition < window.innerHeight * 0.8);
    };
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    return () => window.removeEventListener('touchmove', handleTouchMove);
  }, []);

  const scrollToTop = () =>
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

  const allPets = useMemo(() => data?.pages.flatMap((p) => p.results) ?? [], [data]);
  const total =
    (data?.pages?.[0] as { count?: number } | undefined)?.count ?? allPets.length;

  if (isLoading) return <SolvedCasesSkeleton />;

  return (
    <div className="mx-auto w-full max-w-2xl lg:max-w-3xl pb-10">
      {/* Mint header */}
      <div
        ref={titleRef}
        className="fill-solved relative ml-[calc(50%-50vw)] w-screen px-[calc(50vw-50%+1rem)] pb-5 pt-3"
        style={{ borderBottom: '3px solid var(--nb-ink)' }}
      >
        <div
          className="text-[11px] font-bold uppercase tracking-[0.08em] opacity-70"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          / solved-cases
        </div>
        <div className="font-display mt-1 text-[38px] leading-[0.9]">
          Volvieron
          <br />a casa.
        </div>
        <div className="mt-3 flex items-center gap-3">
          <div className="nb-seal shrink-0" style={{ background: 'var(--nb-paper)' }}>
            <div>
              <div className="font-display text-[24px] leading-none">{total}</div>
              <div
                className="text-[8px] font-bold"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                2026
              </div>
            </div>
          </div>
          <div className="text-[13px] font-semibold">
            Gracias al barrio. Gracias a vos.
            <br />
            <span
              className="text-[10px] opacity-70"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              TASA ÉXITO · GENTE QUE AYUDA
            </span>
          </div>
        </div>
      </div>

      {isError && <div className="px-4 py-16 text-center">Oops, ha ocurrido un error.</div>}
      {allPets.length === 0 && (
        <div className="px-4 py-16 text-center">Aún no hay casos resueltos...</div>
      )}

      {/* Cases — one per row */}
      <div className="flex flex-col gap-3.5 px-4 pt-4">
        {data?.pages.map((page, pageIndex) => (
          <Fragment key={pageIndex}>
            {page.results.map((pet) => (
              <SolvedCard key={pet.id} pet={pet} onOpen={() => navigate(`/pet/${pet.id}`)} />
            ))}
          </Fragment>
        ))}
      </div>

      {hasNextPage && (
        <div className="px-4 pt-5 text-center">
          <NbButton
            fill="paper"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Cargando más…' : 'Cargar más'}
          </NbButton>
        </div>
      )}

      {showScrollButton && (
        <button
          onClick={scrollToTop}
          aria-label="Volver al inicio"
          className="nb-btn fill-urgent fixed bottom-20 right-3 z-20 px-2 py-2"
        >
          <ArrowUpToLineIcon color="black" />
        </button>
      )}
    </div>
  );
};

const SolvedCard: FC<{ pet: Pet; onOpen: () => void }> = ({ pet, onOpen }) => {
  const thumbnail = pet.gallery?.[0];
  const date = useMemo(() => {
    const target = pet.status === 'lost' ? pet.date_lost : pet.date_found;
    return target ? format(new Date(target), 'dd/MM', { locale: es }) : '—';
  }, [pet]);

  const zone = useMemo(() => {
    if (!pet.location?.address) return 'Zona no disponible';
    const { road, city } = pet.location.address;
    return [road, city].filter(Boolean).join(', ') || 'Zona no disponible';
  }, [pet.location]);

  return (
    <NbCard
      className="relative cursor-pointer p-0"
      onClick={onOpen}
    >
      <div className="flex items-center gap-3 p-2.5">
        <div
          className="h-[90px] w-[90px] shrink-0 overflow-hidden"
          style={{ border: '3px solid var(--nb-ink)', background: '#e8e3d5' }}
        >
          {thumbnail ? (
            <img src={thumbnail} alt={pet.name} className="h-full w-full object-cover" />
          ) : (
            <div className="nb-hatch h-full w-full" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="fill-solved inline-block border-2 px-1.5 py-0.5 text-[10px]"
            style={{ borderColor: 'var(--nb-ink)', fontFamily: '"Archivo Black", sans-serif' }}
          >
            ✓ VOLVIÓ A CASA
          </div>
          <div className="font-display mt-1 text-[22px] leading-none">
            {pet.name?.trim() || 'Sin nombre'}
          </div>
          <div className="mt-1 flex items-center gap-1 text-[12px] font-semibold">
            <MapPinIcon size={12} />
            <span className="truncate">{zone}</span>
          </div>
          <div
            className="mt-1 flex gap-2.5 text-[10px] font-bold uppercase opacity-80"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            <span>📍 {date}</span>
          </div>
        </div>
      </div>
      <div className="nb-stamp-rotated">RESUELTO</div>
    </NbCard>
  );
};
